import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import client from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users2, Truck, Gauge, ArrowUpRight, TrendingUp, Infinity as InfinityIcon } from 'lucide-react';

// Returns a tone + pct for a single usage row (cap may be null => unlimited)
function meterState(used, cap) {
  if (cap == null) return { unlimited: true, pct: 0, tone: 'ok' };
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 100;
  let tone = 'ok';
  if (used >= cap) tone = 'over';
  else if (pct >= 80) tone = 'warn';
  return { unlimited: false, pct, tone, over: used > cap };
}

const TONE = {
  ok: { bar: 'bg-[hsl(var(--success))]', text: 'text-[hsl(var(--success))]', label: 'Healthy' },
  warn: { bar: 'bg-[hsl(var(--gold))]', text: 'text-[hsl(var(--gold))]', label: 'Near limit' },
  over: { bar: 'bg-[hsl(var(--danger))]', text: 'text-[hsl(var(--danger))]', label: 'Limit reached' },
};

function Row({ icon: Icon, label, used, cap }) {
  const st = meterState(used, cap);
  const tone = TONE[st.tone];
  return (
    <div data-testid={`usage-row-${label.toLowerCase()}`}>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Icon className="h-4 w-4 text-muted-foreground" />{label}
        </span>
        {st.unlimited ? (
          <span className="flex items-center gap-1 text-sm font-semibold text-foreground" data-testid={`usage-value-${label.toLowerCase()}`}>
            {used} <span className="text-muted-foreground">/</span> <InfinityIcon className="h-4 w-4 text-[hsl(var(--primary))]" />
          </span>
        ) : (
          <span className="text-sm font-semibold tabular-nums text-foreground" data-testid={`usage-value-${label.toLowerCase()}`}>
            {used} <span className="text-muted-foreground">/ {cap}</span>
          </span>
        )}
      </div>
      {st.unlimited ? (
        <div className="h-2 w-full overflow-hidden rounded-full bg-[hsl(var(--primary))]/15">
          <div className="h-full w-full rounded-full bg-gradient-to-r from-[hsl(var(--primary))]/40 to-[hsl(var(--primary))]/10" />
        </div>
      ) : (
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div className={`h-full rounded-full transition-[width] duration-300 ${tone.bar}`} style={{ width: `${st.pct}%` }} />
        </div>
      )}
      <div className="mt-1 flex items-center justify-between text-[11px]">
        <span className={st.unlimited ? 'text-[hsl(var(--primary))] font-medium' : tone.text}>
          {st.unlimited ? 'Unlimited on Corporate' : tone.label}
        </span>
        {!st.unlimited && (
          <span className="text-muted-foreground">{st.over ? `${used - cap} over` : `${Math.max(0, cap - used)} left`}</span>
        )}
      </div>
    </div>
  );
}

function linePath(data, w, h, max) {
  if (!data || data.length === 0) return '';
  const mx = max || 1;
  const n = data.length;
  const step = n > 1 ? w / (n - 1) : 0;
  return data.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - (v / mx) * h).toFixed(1)}`).join(' ');
}

// Tiny 30-day growth chart for drivers & trucks (shared scale for comparability).
function TrendSparkline() {
  const [trend, setTrend] = useState(null);
  useEffect(() => {
    let active = true;
    client.get('/tenant/usage-trend').then((r) => { if (active) setTrend(r.data); }).catch(() => {});
    return () => { active = false; };
  }, []);
  if (!trend || !trend.labels?.length) return null;
  const W = 260, H = 44;
  const maxVal = Math.max(1, ...trend.drivers, ...trend.trucks);
  const dEnd = trend.drivers[trend.drivers.length - 1] - trend.drivers[0];
  const tEnd = trend.trucks[trend.trucks.length - 1] - trend.trucks[0];
  return (
    <div className="mt-4 rounded-xl border bg-muted/20 p-3" data-testid="usage-trend-sparkline">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <TrendingUp className="h-3.5 w-3.5" />Fleet growth · 30 days
        </span>
        <span className="flex items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1 text-[hsl(var(--primary))]"><span className="h-2 w-2 rounded-full bg-[hsl(var(--primary))]" />Drivers {dEnd >= 0 ? `+${dEnd}` : dEnd}</span>
          <span className="flex items-center gap-1 text-[hsl(var(--gold))]"><span className="h-2 w-2 rounded-full bg-[hsl(var(--gold))]" />Trucks {tEnd >= 0 ? `+${tEnd}` : tEnd}</span>
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-11 w-full" role="img" aria-label="30-day fleet growth">
        <path d={linePath(trend.drivers, W, H, maxVal)} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <path d={linePath(trend.trucks, W, H, maxVal)} fill="none" stroke="hsl(var(--gold))" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

/**
 * Live usage-vs-limit meter for the active workspace.
 * variant="compact" is used on the dashboard; "detailed" on settings.
 */
export function UsageMeter({ variant = 'detailed' }) {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  if (!tenant) return null;
  const usage = tenant.usage || {};
  const sub = tenant.subscription || {};
  const maxDrivers = sub.max_drivers;
  const maxTrucks = sub.max_trucks;
  const drivers = usage.drivers || 0;
  const trucks = usage.trucks || 0;

  const dState = meterState(drivers, maxDrivers);
  const tState = meterState(trucks, maxTrucks);
  const limited = maxDrivers != null || maxTrucks != null;
  const nudge = limited && (dState.tone !== 'ok' || tState.tone !== 'ok');
  const atCap = dState.tone === 'over' || tState.tone === 'over';

  return (
    <Card className={`p-5 ${variant === 'compact' ? '' : 'max-w-2xl'}`} data-testid="usage-meter">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display font-semibold">
          <Gauge className="h-5 w-5 text-[hsl(var(--primary))]" />
          Workspace Usage
        </div>
        <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground" data-testid="usage-plan-name">
          {sub.plan_name || '—'} plan
        </span>
      </div>

      <div className="space-y-4">
        <Row icon={Users2} label="Drivers" used={drivers} cap={maxDrivers} />
        <Row icon={Truck} label="Trucks" used={trucks} cap={maxTrucks} />
      </div>

      {nudge && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/10 px-3 py-2.5" data-testid="usage-upgrade-nudge">
          <ArrowUpRight className="h-4 w-4 shrink-0 text-[hsl(var(--gold))]" />
          <p className="min-w-0 flex-1 text-xs text-foreground">
            {atCap
              ? "You've reached your plan limit. Upgrade to Corporate for unlimited drivers and trucks."
              : "You're close to your plan limit. Upgrade to Corporate for unlimited capacity."}
          </p>
          <Button size="sm" className="bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" onClick={() => navigate('/plans')} data-testid="usage-upgrade-button">
            Upgrade
          </Button>
        </div>
      )}
      {!limited && (
        <p className="mt-4 rounded-lg bg-[hsl(var(--primary))]/5 px-3 py-2 text-xs text-muted-foreground" data-testid="usage-unlimited-note">
          Your Corporate plan includes unlimited drivers and trucks.
        </p>
      )}

      <TrendSparkline />
    </Card>
  );
}
